import { Mutex } from "async-mutex";
import { addSeconds } from "date-fns";
import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useState } from "react";
import { omit } from "remeda";
import { logger } from "../../datadog/logger";
import { useIsZoomEnabled } from "../../hooks";
import { useConnectRoomCallback } from "../../session/useConnectRoomCallback";
import { delayedEntryGate } from "../../statsig/gates";
import { Statsig } from "../../statsig/StatsigProvider";
import { toast } from "../../toasts";
import { CallbackResponse } from "../../types";
import { addImmutablyToSet, removeImmutablyFromSet } from "../../utils";
import type { RoomDelegator } from "../../video-sdk";
import {
  participantCountAtom,
  roomAtom,
  roomToDiagnosticInfo,
} from "../../video-sdk";
import { useLazyGetMeasuresByPatientShortId } from "../useGetMeasuresByPatientShortId";
import { getTwilioConfig } from "../../video-sdk/twilio";
import { zoomTokenAtom } from "../../video-sdk/zoom";
import { useUpsertSessionConversationCallback } from "../chat/useUpsertSessionConversationCallback";
import { ClientFinishingUpToastWrapper as ClientFinishingUpToast } from "../delayed-entry/ClientFinishingUpToast";
import { deadlinesByVisitorIdFamily } from "../delayed-entry/state";
import { useCSSRSPromptToastCallback } from "../measures/useCSSRSPromptToastCallback";
import { useClientConsentPromptToastCallback } from "../session-recording/useClientConsentPromptToastCallback";
import { useGetProviderPatientConsent } from "../session-recording/useGetProviderPatientConsent";
import {
  drawerStateAtom,
  inSessionPatientInformationAtom,
  joiningVisitorIdsAtom,
  ProviderDrawerState,
  providerShortIdAtom,
  userDataAtom,
  visitorAdmissionTimesAtom,
  visitorPresenceAtom,
} from "../state";
import { useProviderDisconnectRoomCallback } from "../useProviderDisconnectRoomCallback";
import { useCheckChattyVisitorCallback } from "../useVisitorPresenceMap";
import {
  MeetingAccessEventType,
  useAdmitVisitorMutation,
} from "./useAdmitVisitorMutation";
import { useGetRoomTokenCallback } from "./useGetRoomTokenCallback";
import { useWaitForClientToJoinCallback } from "./useWaitForClientToJoinCallback";

function showErrorToast(message: string) {
  toast.error(message, { purpose: "system" });
}

export enum CreateRoomStatusCode {
  CREATED = "CREATED",
  ALREADY_EXISTS = "ALREADY_EXISTS",
}

export type AdmitClientCallback = (
  visitorUuid: string,
  visitorName: string,
  patientShortId: string,
) => Promise<void | NodeJS.Timeout>;

export function useGracefullyRequestVisitorAdmissionCallback() {
  const [admitVisitor] = useAdmitVisitorMutation();
  // This atom is expected to always be set so long as a provider is logged in.
  const getProviderShortId = useAtomCallback(
    useCallback((get) => get(providerShortIdAtom), []),
  );
  const setDrawerState = useSetAtom(drawerStateAtom);
  const setInSessionPatientInformation = useSetAtom(
    inSessionPatientInformationAtom,
  );
  const getInSessionPatientInformation = useAtomCallback(
    useCallback((get) => get(inSessionPatientInformationAtom), []),
  );
  const getDeadline = useAtomCallback(
    useCallback(
      (get, _, visitorId: string) => get(deadlinesByVisitorIdFamily(visitorId)),
      [],
    ),
  );

  const [fetchMeasures] = useLazyGetMeasuresByPatientShortId();
  const { refetch: fetchConsents } = useGetProviderPatientConsent();
  const maybeShowCSSRSPromptToast = useCSSRSPromptToastCallback();
  const maybeShowClientConsentPromptToast =
    useClientConsentPromptToastCallback();
  const setVisitorAdmissionTimes = useSetAtom(visitorAdmissionTimesAtom);

  return useCallback(
    async ({
      conversationSid,
      room,
      visitorName,
      visitorUuid,
      patientShortId,
      appointmentShortId,
      isZoomEnabled,
    }: {
      conversationSid?: string;
      room: RoomDelegator;
      visitorName: string;
      visitorUuid: string;
      patientShortId: string;
      appointmentShortId?: string;
      isZoomEnabled?: boolean;
    }) => {
      const providerShortId = getProviderShortId();
      try {
        const deadline = getDeadline(visitorUuid);
        const admittedVisitor = await admitVisitor({
          variables: {
            conversationSid,
            eventType: MeetingAccessEventType.GRANTED,
            roomName: room.name,
            visitorName,
            visitorUuid,
            patientShortId,
            isZoomEnabled,
            ...(deadline && { deadline }),
          },
        });
        const inSessionPatient = getInSessionPatientInformation();
        setInSessionPatientInformation({
          patientShortId,
          visitorUuid,
          name: visitorName,
          inSessionAppointmentShortId: appointmentShortId,
        });
        setVisitorAdmissionTimes((prev) => ({
          ...prev,
          [visitorUuid]:
            admittedVisitor.data?.updateTwilioVisitorMeetingAccess
              .admissionTime,
        }));

        if (inSessionPatient?.patientShortId !== patientShortId) {
          // The following post-admission side effects are not session critical
          // and should not prevent a visitor from actually being admitted.
          try {
            const measuresResponse = await fetchMeasures({
              variables: { patientShortId },
            });
            maybeShowCSSRSPromptToast(
              { visitorName, visitorUuid, patientShortId },
              measuresResponse.data?.patientChart,
            );
          } catch (error) {
            logger.error(
              "Unable to fetch patient information",
              {
                patientShortId,
                visitorUuid,
              },
              error as Error,
            );
          }

          try {
            if (providerShortId && patientShortId) {
              const consentResponse = await fetchConsents({
                providerShortId,
                patientShortId,
              });
              maybeShowClientConsentPromptToast(
                consentResponse.data,
                patientShortId,
                appointmentShortId,
              );
            } else {
              logger.info(
                "Not fetching consent statuses because providerShortId or patientShortId is not available",
                {
                  patientShortId,
                  providerShortId,
                },
              );
            }
          } catch (error) {
            logger.error(
              "Unable to fetch consent statuses ",
              {
                patientShortId,
                visitorUuid,
              },
              error as Error,
            );
          }
        }

        if (Statsig.checkGate(delayedEntryGate)) {
          toast.enqueue(
            (ariaProps) => (
              <ClientFinishingUpToast
                {...ariaProps}
                visitorId={visitorUuid}
                participantName={visitorName}
                onOpenQueue={() => {
                  setDrawerState(ProviderDrawerState.CLIENTS);
                  ariaProps.closeToast();
                }}
              />
            ),
            { purpose: "realtime", timeout: Infinity },
          );
        }

        return admittedVisitor;
      } catch (error) {
        showErrorToast("There was an issue admitting the client");
        logger.error(
          "Unable to admit visitor",
          {
            providerShortId,
            visitorUuid,
            ...roomToDiagnosticInfo(room),
          },
          error as Error,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

const roomMutex = new Mutex();

export function useGetOrConnectRoomCallback() {
  const getRoom = useAtomCallback(useCallback((get) => get(roomAtom), []));
  const getVisitorById = useAtomCallback(
    useCallback((get) => get(visitorPresenceAtom), []),
  );

  const getRoomToken = useGetRoomTokenCallback();
  const [connectRoom] = useConnectRoomCallback();
  const setZoomToken = useSetAtom(zoomTokenAtom);
  const [, checkZoomIsEnabled] = useIsZoomEnabled();
  const getProviderName = useAtomCallback(
    useCallback((get) => get(userDataAtom)?.name, []),
  );

  return useCallback(
    async (
      visitorId: string,
    ): Promise<
      | undefined
      | (CallbackResponse<RoomDelegator> & {
          value: RoomDelegator;
          appointmentShortId: string | undefined;
        })
    > => {
      const releaseMutex = await roomMutex.acquire();
      try {
        const { patientShortId } = getVisitorById()[visitorId] ?? {};
        const roomTokenData = await getRoomToken(patientShortId);

        const existingRoom = getRoom();
        if (existingRoom) {
          return {
            status: "success",
            code: CreateRoomStatusCode.ALREADY_EXISTS,
            value: existingRoom,
            appointmentShortId: roomTokenData?.appointmentShortId,
          };
        }

        if (!roomTokenData?.token) {
          showErrorToast("There was an issue creating the room");
          return;
        }

        if (roomTokenData?.zoomJwt) {
          setZoomToken(roomTokenData.zoomJwt);
        }

        const tokenOverride = checkZoomIsEnabled()
          ? roomTokenData.zoomJwt
          : roomTokenData.token;
        const newRoom = await connectRoom(
          getProviderName() ?? "Your Provider",
          { tokenOverride },
        );
        if (!newRoom) {
          showErrorToast("There was an issue connecting to the room");
          return;
        }

        return {
          status: "success",
          code: CreateRoomStatusCode.CREATED,
          value: newRoom,
          appointmentShortId: roomTokenData?.appointmentShortId,
        };
      } finally {
        releaseMutex();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

/**
 * Returns a callback fn that can be used to admit a patient to the waiting
 * room. It will load until a participant connects to the room or the timeout is
 * reached. It will also indicating the visitor it's waiting for in Jotai.
 */
export function useAdmitClientCallback(): [
  AdmitClientCallback,
  { loading?: boolean },
] {
  const [waitingForParticipant, setWaitingForParticipant] = useState<boolean>();
  const getParticipantCount = useAtomCallback(
    useCallback((get) => get(participantCountAtom), []),
  );
  const loading = waitingForParticipant;
  const getOrConnectRoom = useGetOrConnectRoomCallback();
  const setJoiningVisitorIds = useSetAtom(joiningVisitorIdsAtom);
  const belowParticipantCapacity = useCallback(
    () => {
      if (getParticipantCount() >= getTwilioConfig().maxParticipants - 1) {
        showErrorToast(
          "You have reached the maximum number of participants allowed in a session",
        );
        return false;
      }
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const setVisitors = useSetAtom(visitorPresenceAtom);
  const gracefullyRequestVisitorAdmission =
    useGracefullyRequestVisitorAdmissionCallback();
  const checkChattyVisitor = useCheckChattyVisitorCallback();
  const stopWaitingForClientToJoin = async (visitorId: string) => {
    if (!(await checkChattyVisitor(visitorId))) {
      setVisitors((prev) => omit(prev, [visitorId]));
    }
    setJoiningVisitorIds((prev) => removeImmutablyFromSet(prev, visitorId));
    setWaitingForParticipant(false);
  };
  const setVisitorDeadline = useAtomCallback(
    useCallback(
      (_, set, visitorId: string, deadline: Date | null) =>
        set(deadlinesByVisitorIdFamily(visitorId), deadline),
      [],
    ),
  );
  const startWaitingForClientToJoin = (visitorId: string) => {
    setJoiningVisitorIds((prev) => addImmutablyToSet(prev, visitorId));
    setWaitingForParticipant(true);
    setVisitorDeadline(visitorId, addSeconds(new Date(), 30));
  };
  const disconnectRoom = useProviderDisconnectRoomCallback();
  const disconnectFromCreatedRoom = (code?: string) => {
    if (code === CreateRoomStatusCode.CREATED && !getParticipantCount()) {
      disconnectRoom();
    }
  };
  const waitForClientToJoin = useWaitForClientToJoinCallback();
  const upsertConversationCallback = useUpsertSessionConversationCallback();
  const [, checkZoomIsEnabled] = useIsZoomEnabled();

  const callback = useCallback<AdmitClientCallback>(
    async (
      visitorId: string,
      visitorName: string,
      patientShortId: string,
    ): Promise<void | NodeJS.Timeout> => {
      startWaitingForClientToJoin(visitorId);
      let createRoomResult: Awaited<ReturnType<typeof getOrConnectRoom>>;
      let upsertConversationResult: Awaited<
        ReturnType<typeof upsertConversationCallback>
      >;
      if (
        !belowParticipantCapacity() ||
        !(createRoomResult = await getOrConnectRoom(visitorId)) ||
        // Chat creation is a side-effect that should not stop admission
        ((upsertConversationResult =
          await upsertConversationCallback(visitorId)) &&
          false) ||
        !(await gracefullyRequestVisitorAdmission({
          room: createRoomResult.value,
          visitorName,
          visitorUuid: visitorId,
          conversationSid: upsertConversationResult?.value?.sid,
          patientShortId,
          appointmentShortId: createRoomResult?.appointmentShortId,
          isZoomEnabled: checkZoomIsEnabled(),
        }))
      ) {
        await stopWaitingForClientToJoin(visitorId);
        disconnectFromCreatedRoom(createRoomResult?.code);
        return;
      }

      return waitForClientToJoin({
        visitorId,
        onTimeout: () => {
          stopWaitingForClientToJoin(visitorId);
          setVisitorDeadline(visitorId, null);
        },
        onFailure: () => {
          disconnectFromCreatedRoom(createRoomResult?.code);
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return [callback, { loading }];
}
